import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { motion, AnimatePresence } from 'framer-motion';

// --- Configuration (SistemiPos Brand Style) ---
const BRAND_PRIMARY = '#333';   // Dark Gray for "Sistemi"
const BRAND_SECONDARY = '#D16666'; // SistemiPos Red for "Pos"
const PARTICLE_COLOR = '#C4C4C4'; // Neutral gray for particles

// --- Animation Variants for Framer Motion ---

const sentenceVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.2,
      staggerChildren: 0.08,
    },
  },
};

const letterVariant = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 12,
      stiffness: 200,
    },
  },
};

// --- Particle Configuration ---
// Defines the properties of each orbiting element
const particles = [
  { size: 8, radius: 80, duration: 10, direction: 1 },  // Small, close, fast
  { size: 8, radius: 80, duration: 10, direction: -1 }, // Mirrored
  { size: 12, radius: 120, duration: 15, direction: 1 }, // Medium, far, slow
  { size: 6, radius: 100, duration: 18, direction: -1 }, // Small, medium, very slow
  { size: 10, radius: 60, duration: 8, direction: 1 },   // Medium, very close, very fast
];


const WaitingDialog = ({ open, message = 'Caricamento in corso...' }) => {
  const brandName = "Sistemi";
  const brandSuffix = "Pos";

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            style: {
              backgroundColor: '#FFFFFF',
              borderRadius: '24px',
              boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
              userSelect: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: '#F8F9FA' }}>
              <Box
                sx={{
                  position: 'relative',
                  height: '280px', // Increased height for more space
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden', // Ensures particles don't escape the box
                }}
              >
                {/* --- ORBITING PARTICLES --- */}
                {particles.map((p, i) => (
                  <motion.div
                    key={i}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: p.radius * 2,
                      height: p.radius * 2,
                      x: '-50%',
                      y: '-50%',
                    }}
                    animate={{ rotate: 360 * p.direction }}
                    transition={{
                      duration: p.duration,
                      ease: 'linear',
                      repeat: Infinity,
                    }}
                  >
                    <motion.div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0, // Start at the edge of the rotating container
                        width: p.size,
                        height: p.size,
                        backgroundColor: PARTICLE_COLOR,
                        borderRadius: '50%',
                        x: `-${p.size/2}px`, // Center the particle on the path
                        y: `-${p.size/2}px`,
                        boxShadow: `0 0 8px ${PARTICLE_COLOR}` // Glow effect
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.8 + i * 0.1, duration: 0.5 }}
                    />
                  </motion.div>
                ))}


                {/* --- CENTRAL LOGO --- */}
                <Box sx={{ zIndex: 1 }}>
                  <motion.div
                    variants={sentenceVariant}
                    initial="hidden"
                    animate="visible"
                  >
                    <Typography variant="h2" component="div" sx={{ fontWeight: 'bold' }}>
                      {brandName.split("").map((char, index) => (
                        <motion.span key={`${char}-${index}`} variants={letterVariant} style={{ color: BRAND_PRIMARY, display: 'inline-block' }}>
                          {char}
                        </motion.span>
                      ))}
                      {brandSuffix.split("").map((char, index) => (
                        <motion.span key={`${char}-${index}`} variants={letterVariant} style={{ color: BRAND_SECONDARY, display: 'inline-block' }}>
                          {char}
                        </motion.span>
                      ))}
                    </Typography>
                  </motion.div>
                </Box>
              </Box>

              <Box sx={{ py: 2, px: 3, borderTop: '1px solid #E0E0E0' }}>
                <Typography variant="body1" sx={{ color: '#3c4043', fontWeight: '500' }}>
                  {message}
                </Typography>
              </Box>
            </DialogContent>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default WaitingDialog;